import * as React from "react";
import { ArrowLeftToLineIcon, ArrowRightToLineIcon, Building2, Mail, Settings2, Users, MenuIcon } from "lucide-react";
import { useToggle } from "react-use";

import { ProfileMenu } from "./ProfileMenu";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/ui/userAvatar";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { Link } from "react-router";
import { NavigationMenuList } from "@/components/sidebar/NavigationMenuList";
import { SystemVersion } from "./SystemVersion";
import { ThirdPartyNoticesLink } from "./ThirdPartyNoticesLink";
import { SidebarFavorites } from "@/modules/favorites/SidebarFavorites";
import { SidebarIcons } from "@/components/sidebar/SidebarIcons";
import { OrganizationSettingsModal } from "@/components/modals/organizationSettings/OrganizationSettingsModal";
import { SnapshotEventMailModal } from "@/modules/snapshots/snapshotEvents/snapshotEventMailModal";
import { ContractFamilyConfigurationModal } from "@/components/modals/ContractFamilyConfigurationModal";

interface SidebarNavProps {
  isPinned: boolean;
  onPinChange: (isPinned: boolean) => void;
}

export const SidebarNav: React.FC<SidebarNavProps> = ({ isPinned, onPinChange }) => {
  const user = useAuthenticatedUser();
  const [isOrgSettingsOpen, toggleOrgSettings] = useToggle(false);
  const [isContractFamilyConfigurationOpen, toggleContractFamilyConfiguration] = useToggle(false);
  const [isMessagesModalOpen, toggleMessagesModal] = useToggle(false);

  const togglePin = React.useCallback(() => {
    onPinChange(!isPinned);
  }, [isPinned, onPinChange]);

  return (
    <div className="relative flex" data-testid="sidebar">
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-72",
          "transition-all duration-300 ease-in-out",
          isPinned ? "translate-x-0" : "-translate-x-[calc(100%-56px)]",
          "backdrop-blur-md",
          isPinned ? "bg-ui-sidebar-background-rgba" : "bg-whites",
        )}
        style={
          {
            "--tw-bg-opacity": isPinned ? "1" : "0.2",
          } as React.CSSProperties
        }
      >
        <div
          className={`text-atriablue-500 grid h-full grid-rows-[auto,1fr,auto] overflow-y-auto ${
            isPinned ? "shadow-md" : ""
          }`}
        >
          <div className="flex items-center justify-between px-4 pb-2 pt-10">
            <div className="flex-shrink-0">
              <img src="/images/pathfinder_logo_beta.svg" alt="Pathfinder logo" width={160} height={110} />
            </div>
            {isPinned && (
              <button
                type="button"
                className="rounded-full p-2 text-gray-400 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-500"
                onClick={togglePin}
                aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
              >
                {isPinned ? (
                  <ArrowLeftToLineIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <ArrowRightToLineIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
          <div className={`flex flex-col px-2 ${!isPinned ? "invisible" : ""}`}>
            <div className="flex h-full flex-col">
              <nav className="flex-1">
                <NavigationMenuList />
                <SidebarFavorites />
              </nav>
              <div className="mb-4 mt-auto">
                <button
                  type="button"
                  onClick={toggleMessagesModal}
                  className="text-atriablue-500 group flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-sm font-semibold leading-6 transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
                >
                  <Mail className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>Messages</span>
                </button>
                {user.features.includes("user_admin") && (
                  <Link
                    to="/admin/users"
                    className="text-atria blue-500 group flex items-center gap-x-2 rounded-md px-2 py-1.5 text-sm font-semibold leading-6 transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
                  >
                    <Users className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>Users</span>
                  </Link>
                )}
                {user.features.includes("superadmin") && (
                  <button
                    type="button"
                    onClick={toggleOrgSettings}
                    className="text-atriablue-500 group flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-sm font-semibold leading-6 transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
                  >
                    <Building2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>Organization</span>
                  </button>
                )}
                {user.features.includes("contract_families") &&
                  (user.pfRoles.includes("organization_admin") || user.pfRoles.includes("superadmin")) && (
                    <button
                      type="button"
                      onClick={toggleContractFamilyConfiguration}
                      className="text-atriablue-500 group flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-sm font-semibold leading-6 transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
                    >
                      <Settings2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span>Contract Family settings</span>
                    </button>
                  )}
                <ProfileMenu />
                <div className="flex items-center justify-between">
                  <SystemVersion />
                  <ThirdPartyNoticesLink />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isPinned && (
        <div className="fixed inset-y-0 left-0 z-50 flex flex-col items-center bg-ui-sidebar-background-rgba backdrop-blur-md">
          <div className="flex h-[88px] w-full flex-col items-center justify-center px-2 pt-4">
            <button
              className="rounded-md p-3 text-gray-500 hover:bg-gray-100 hover:text-gray-600"
              onClick={togglePin}
              aria-label="Open sidebar"
            >
              <MenuIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <SidebarIcons />

          <div className="flex-grow" />
          <div className="pointer-events-auto ml-0 flex w-full flex-col space-y-4 px-2 pb-4">
            <button className="overflow-hidden rounded-full p-1.5" onClick={togglePin}>
              <UserAvatar user={user} size="sm" />
            </button>
          </div>
        </div>
      )}

      <OrganizationSettingsModal open={isOrgSettingsOpen} onOpenChange={toggleOrgSettings} />
      <ContractFamilyConfigurationModal
        open={isContractFamilyConfigurationOpen}
        onOpenChange={toggleContractFamilyConfiguration}
      />
      <SnapshotEventMailModal hideFooter open={isMessagesModalOpen} onClose={toggleMessagesModal} />
    </div>
  );
};
